import { useState } from 'react';

import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { useUpdateCandidateNoticeMutation } from '@/modules/legal/mutations/useUpdateCandidateNoticeMutation';
import { useCandidateNoticesQuery } from '@/modules/legal/queries/useCandidateNoticesQuery';
import { useLegalDocumentsQuery } from '@/modules/legal/queries/useLegalDocumentsQuery';
import type {
  CandidateNoticeApi,
  LegalDocumentTypeApi,
} from '@/modules/legal/types';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { CandidateNoticeSetting } from './candidate-notice-setting/CandidateNoticeSetting';

interface CandidateNoticesSectionProps {
  tenantId: string;
}

/**
 * The tenant's candidate notices on the Workspace page: Finlay's privacy
 * notice and the AI disclosure line, each on by default.
 */
export function CandidateNoticesSection({
  tenantId,
}: CandidateNoticesSectionProps) {
  const noticesQuery = useCandidateNoticesQuery(tenantId);
  const documentsQuery = useLegalDocumentsQuery();
  const updateMutation = useUpdateCandidateNoticeMutation();
  const [recordedOff, setRecordedOff] = useState<CandidateNoticeApi[]>([]);
  const notices = noticesQuery.data;

  const documentText = (type: LegalDocumentTypeApi) =>
    documentsQuery.data?.find((document) => document.type === type)?.text ??
    undefined;
  const aiDisclosureText = documentText('AiDisclosure');

  const change = (notice: CandidateNoticeApi, enabled: boolean) => {
    updateMutation.mutate(
      { tenantId, notice, enabled },
      {
        onSuccess: () =>
          setRecordedOff((current) =>
            enabled
              ? current.filter((item) => item !== notice)
              : [...current, notice],
          ),
      },
    );
  };

  const savingNotice = updateMutation.isPending
    ? updateMutation.variables.notice
    : null;
  const errorFor = (notice: CandidateNoticeApi) =>
    updateMutation.isError && updateMutation.variables?.notice === notice
      ? actionErrorMessage('save this setting', updateMutation.error)
      : null;

  return (
    <section
      aria-labelledby="candidate-notices"
      className="flex flex-col gap-1"
    >
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[15px] font-semibold" id="candidate-notices">
          Candidate notices
        </h2>
        <p className="text-muted-foreground text-[13px]">
          What candidates are told when Finlay contacts them for you.
        </p>
      </div>

      {noticesQuery.isPending ? (
        <Skeleton className="my-4 h-16 w-full" />
      ) : noticesQuery.isError || !notices ? (
        <HMLoadError
          description="We could not load these settings. Check your connection and try again."
          isRetrying={noticesQuery.isFetching}
          onRetry={() => void noticesQuery.refetch()}
          title="Candidate notices could not be loaded"
        />
      ) : (
        <div className="divide-border flex flex-col divide-y border-y">
          <CandidateNoticeSetting
            description="Finlay links its privacy notice after a candidate first replies."
            enabled={notices.privacyNoticeEnabled}
            error={errorFor('PrivacyNotice')}
            isOffRecorded={recordedOff.includes('PrivacyNotice')}
            isSaving={savingNotice === 'PrivacyNotice'}
            offWarning={documentText('PrivacyNoticeOffWarning')}
            onChange={(enabled) => change('PrivacyNotice', enabled)}
            title="Privacy notice"
          />
          <CandidateNoticeSetting
            description={
              aiDisclosureText
                ? `The first message to each candidate ends with “${aiDisclosureText}”`
                : 'The first message to each candidate says it was sent by an AI assistant.'
            }
            enabled={notices.aiDisclosureEnabled}
            error={errorFor('AiDisclosure')}
            isOffRecorded={recordedOff.includes('AiDisclosure')}
            isSaving={savingNotice === 'AiDisclosure'}
            offWarning={documentText('AiDisclosureOffWarning')}
            onChange={(enabled) => change('AiDisclosure', enabled)}
            title="AI disclosure"
          />
        </div>
      )}
    </section>
  );
}
